import React, {useEffect, useRef} from 'react';
import {Col, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';

export interface UserAuditSummaryChartsProps {
    dayKeys: string[];
    axisLabels: string[];
    loginCounts: number[];
    requestCounts: number[];
}

const CHART_HEIGHT_PX = 280;
const TARGET_AXIS_LABELS = 12;

function tooltipDayTitle(dayKey: string): string {
    try {
        return DateTime.fromISO(dayKey, {zone: 'utc'}).toLocaleString(DateTime.DATE_MED);
    } catch {
        return dayKey;
    }
}

const UserAuditSummaryCharts: React.FC<UserAuditSummaryChartsProps> = ({
    dayKeys,
    axisLabels,
    loginCounts,
    requestCounts,
}) => {
    const loginRef = useRef<HTMLDivElement>(null);
    const requestsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const loginEl = loginRef.current;
        const reqEl = requestsRef.current;
        if (!loginEl || !reqEl) {
            return;
        }

        const loginChart = echarts.init(loginEl);
        const reqChart = echarts.init(reqEl);

        const handleResize = () => {
            if (!loginChart.isDisposed()) {
                loginChart.resize();
            }
            if (!reqChart.isDisposed()) {
                reqChart.resize();
            }
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(loginEl);
            resizeObserver.observe(reqEl);
        }
        window.addEventListener('resize', handleResize);

        const dayCount = axisLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / TARGET_AXIS_LABELS) - 1);
        const rotate = dayCount > 10 ? 28 : 0;

        const axisLabel = {
            rotate,
            interval: labelInterval,
            hideOverlap: true,
        };

        const tooltipAxis = {
            trigger: 'axis' as const,
            axisPointer: {type: 'shadow' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const lines = params.map(
                    (p) => `${p.marker} ${p.seriesName}: <strong>${p.value}</strong>`,
                );
                return `${title}<br/>${lines.join('<br/>')}`;
            },
        };

        loginChart.setOption({
            textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
            tooltip: tooltipAxis,
            grid: {left: 44, right: 12, top: 12, bottom: rotate ? 48 : 24},
            xAxis: {
                type: 'category',
                data: axisLabels,
                axisLabel,
            },
            yAxis: {type: 'value', minInterval: 1},
            series: [
                {
                    name: i18n.t('ub_chart_logins_series.text'),
                    type: 'bar',
                    data: loginCounts,
                },
            ],
        }, true);

        reqChart.setOption({
            textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
            tooltip: tooltipAxis,
            grid: {left: 44, right: 12, top: 12, bottom: rotate ? 48 : 24},
            xAxis: {
                type: 'category',
                data: axisLabels,
                axisLabel,
            },
            yAxis: {type: 'value', minInterval: 1},
            series: [
                {
                    name: i18n.t('ub_chart_requests_series.text'),
                    type: 'bar',
                    data: requestCounts,
                },
            ],
        }, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            loginChart.dispose();
            reqChart.dispose();
        };
    }, [dayKeys, axisLabels, loginCounts, requestCounts]);

    return (
        <Row className="g-3">
            <Col md={6}>
                <h6 className="mb-2">{i18n.t('ub_chart_logins_per_day.text')}</h6>
                <div ref={loginRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
            </Col>
            <Col md={6}>
                <h6 className="mb-2">{i18n.t('ub_chart_endpoint_requests_per_day.text')}</h6>
                <div ref={requestsRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
            </Col>
        </Row>
    );
};

export default UserAuditSummaryCharts;
